import { useCallback, useEffect, useRef, useState } from "react";
import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  FormLayout,
  InlineStack,
  Modal,
  Text,
  TextField,
} from "@shopify/polaris";
import supabase from "../../supabase-client";
import { PAYEE_TYPE_LABEL, payeeFormatErrors, rpcError, searchPayees } from "../../utility/egresos";
import PayeeFields from "./PayeeFields";

// Who we pay. Searches providers, clients, companies, trasladistas and
// terceros at once. If the person exists nowhere, "Nuevo tercero" creates a
// profile that is kept for next time — and refuses (find_party_matches) when
// the RFC, CURP, CLABE or phone already belongs to someone, offering that
// profile instead, so one person never ends up with two profiles (or two CLABEs).

const DEBOUNCE_MS = 250;

// `prefill` ({ name, rfc, clabe }) is what a document said about an
// issuer we don't have yet: it fills the "Nuevo tercero" form, never a payee.
export default function PayeePicker({ value, onChange, label = "¿A quién se le paga?", prefill = null }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const seq = useRef(0);

  const search = useCallback(async (q) => {
    const mine = ++seq.current;
    if (q.trim().length < 2) {
      setResults(null);
      return;
    }
    setLoading(true);
    const rows = await searchPayees(q);
    if (mine !== seq.current) return;
    setResults(rows);
    setLoading(false);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => search(query), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query, search]);

  if (value) {
    return (
      <BlockStack gap="100">
        <Text as="span" variant="bodyMd">{label}</Text>
        <Box borderWidth="025" borderColor="border" borderRadius="200" padding="300">
          <InlineStack align="space-between" blockAlign="center">
            <BlockStack gap="050">
              <Text as="span" fontWeight="semibold">{value.name}</Text>
              {value.detail && <Text as="span" tone="subdued" variant="bodySm">{value.detail}</Text>}
            </BlockStack>
            <InlineStack gap="200" blockAlign="center">
              <Badge>{PAYEE_TYPE_LABEL[value.payee_type]}</Badge>
              <Button variant="plain" onClick={() => onChange(null)}>Cambiar</Button>
            </InlineStack>
          </InlineStack>
        </Box>
      </BlockStack>
    );
  }

  return (
    <BlockStack gap="200">
      <TextField
        label={label}
        requiredIndicator
        value={query}
        onChange={setQuery}
        placeholder="Proveedor, cliente, empresa, trasladista o tercero"
        autoComplete="off"
        loading={loading}
        clearButton
        onClearButtonClick={() => setQuery("")}
        connectedRight={<Button onClick={() => setCreating(true)}>Nuevo tercero</Button>}
      />
      {prefill?.name && (
        <Box padding="300" borderRadius="200" background="bg-surface-secondary">
          <InlineStack align="space-between" blockAlign="center" gap="200">
            <Text>
              <b>{prefill.name}</b>
              {prefill.rfc ? ` (${prefill.rfc})` : ""} no está registrado.
            </Text>
            <Button onClick={() => setCreating(true)}>Crear con estos datos</Button>
          </InlineStack>
        </Box>
      )}
      {results !== null && results.length === 0 && !loading && (
        <Text tone="subdued" variant="bodySm">Sin resultados. Si es una persona nueva, créala como tercero.</Text>
      )}
      {results?.map((r) => (
        <Box key={`${r.payee_type}:${r.payee_id}`} borderWidth="025" borderColor="border" borderRadius="200" padding="300">
          <div
            role="button"
            tabIndex={0}
            style={{ cursor: "pointer" }}
            onClick={() => onChange(r)}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onChange(r)}
          >
            <InlineStack align="space-between" blockAlign="center" wrap={false}>
              <BlockStack gap="050">
                <Text as="span" fontWeight="semibold">{r.name}</Text>
                {r.detail && <Text as="span" tone="subdued" variant="bodySm">{r.detail}</Text>}
              </BlockStack>
              <Badge>{PAYEE_TYPE_LABEL[r.payee_type]}</Badge>
            </InlineStack>
          </div>
        </Box>
      ))}
      {creating && (
        <NewPayeeModal
          initial={prefill || { name: query }}
          onClose={() => setCreating(false)}
          onPicked={(p) => {
            setCreating(false);
            onChange(p);
          }}
        />
      )}
    </BlockStack>
  );
}

const EMPTY = { name: "", nickname: "", phone: "", email: "", rfc: "", curp: "", clabe: "", bank_holder_name: "", spei_concept: "", notes: "" };

function NewPayeeModal({ initial, onClose, onPicked }) {
  const [f, setF] = useState({
    ...EMPTY,
    ...Object.fromEntries(Object.entries(initial || {}).filter(([k, v]) => k in EMPTY && v)),
  });
  const [company, setCompany] = useState(null);
  const [companyQuery, setCompanyQuery] = useState("");
  const [companyResults, setCompanyResults] = useState([]);
  const [matches, setMatches] = useState([]);
  const [overrideReason, setOverrideReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  // Live duplicate check while typing: exact hits block, name hits warn.
  useEffect(() => {
    const t = setTimeout(async () => {
      if (!f.rfc && !f.curp && !f.clabe && !f.phone && f.name.trim().length < 5) return setMatches([]);
      const { data } = await supabase.rpc("find_party_matches", {
        p_rfc: f.rfc || null,
        p_curp: f.curp || null,
        p_clabe: f.clabe || null,
        p_phone: f.phone || null,
        p_email: f.email || null,
        p_name: f.name || null,
      });
      setMatches(data || []);
    }, 300);
    return () => clearTimeout(t);
  }, [f.rfc, f.curp, f.clabe, f.phone, f.email, f.name]);

  useEffect(() => {
    if (companyQuery.trim().length < 2) return setCompanyResults([]);
    const t = setTimeout(async () => {
      const { data } = await supabase.rpc("search_clients", { search_text: companyQuery });
      setCompanyResults((data || []).filter((c) => c.client_type === "company").slice(0, 5));
    }, 250);
    return () => clearTimeout(t);
  }, [companyQuery]);

  const exact = matches.filter((m) => m.exact);
  const similar = matches.filter((m) => !m.exact);

  const save = async () => {
    setSaving(true);
    setError(null);
    const { data, error: err } = await supabase.rpc("create_payee", {
      p: { ...f, company_id: company?.id || null },
      p_override_reason: exact.length ? overrideReason : null,
    });
    setSaving(false);
    if (err) return setError(rpcError(err));
    onPicked({
      payee_type: "payee",
      payee_id: data,
      name: f.nickname.trim() || f.name.trim(),
      detail: [f.nickname.trim() && f.name.trim(), company && `de ${company.name}`, f.phone].filter(Boolean).join(" · "),
    });
  };

  const usable = (exact.length === 0 || overrideReason.trim().length > 0) && Object.keys(payeeFormatErrors(f)).length === 0;

  return (
    <Modal
      open
      onClose={onClose}
      title="Nuevo tercero"
      primaryAction={{ content: "Crear", onAction: save, loading: saving, disabled: !f.name.trim() || !usable }}
      secondaryActions={[{ content: "Cancelar", onAction: onClose }]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          <Text tone="subdued">
            Para personas o negocios que no son clientes, proveedores de servicios ni trasladistas (p. ej. el operador
            de una empresa cliente, o una mueblería). El perfil se guarda y se reutiliza; su CLABE es la misma cada vez.
          </Text>
          {error && <Banner tone="critical">{error}</Banner>}
          {exact.length > 0 && (
            <Banner tone="warning" title="Esta persona ya existe">
              <BlockStack gap="200">
                {exact.map((m) => (
                  <InlineStack key={`${m.party_type}:${m.party_id}:${m.matched_on}`} align="space-between" blockAlign="center">
                    <Text>
                      {m.name} · {PAYEE_TYPE_LABEL[m.party_type]} · coincide por {m.matched_on}
                    </Text>
                    <Button
                      size="slim"
                      onClick={() => onPicked({ payee_type: m.party_type, payee_id: m.party_id, name: m.name, detail: "" })}
                    >
                      Usar este perfil
                    </Button>
                  </InlineStack>
                ))}
                <TextField
                  label="Si de verdad es otra persona, explica por qué"
                  value={overrideReason}
                  onChange={setOverrideReason}
                  autoComplete="off"
                />
              </BlockStack>
            </Banner>
          )}
          {exact.length === 0 && similar.length > 0 && (
            <Banner tone="info" title="Nombres parecidos">
              <BlockStack gap="100">
                {similar.slice(0, 5).map((m) => (
                  <InlineStack key={`${m.party_type}:${m.party_id}:${m.matched_on}`} align="space-between" blockAlign="center">
                    <Text>{m.name} · {PAYEE_TYPE_LABEL[m.party_type]}</Text>
                    <Button
                      size="slim"
                      variant="plain"
                      onClick={() => onPicked({ payee_type: m.party_type, payee_id: m.party_id, name: m.name, detail: "" })}
                    >
                      Usar
                    </Button>
                  </InlineStack>
                ))}
              </BlockStack>
            </Banner>
          )}
          <PayeeFields f={f} set={set} />
          <FormLayout>
            {company ? (
              <InlineStack gap="200" blockAlign="center">
                <Text>Trabaja para: <b>{company.name}</b></Text>
                <Button variant="plain" onClick={() => setCompany(null)}>Quitar</Button>
              </InlineStack>
            ) : (
              <BlockStack gap="100">
                <TextField
                  label="Empresa para la que trabaja"
                  value={companyQuery}
                  onChange={setCompanyQuery}
                  autoComplete="off"
                />
                {companyResults.map((c) => (
                  <Button key={c.id} variant="plain" textAlign="left" onClick={() => setCompany(c)}>
                    {c.name}
                  </Button>
                ))}
              </BlockStack>
            )}
            <TextField label="Notas" value={f.notes} onChange={set("notes")} multiline={2} autoComplete="off" />
          </FormLayout>
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}
